interface FunnelStep {
  label: string;
  value: number;
  color?: string;
  /** Short grey caption under the step label (right column before bars). */
  summary?: string;
  /**
   * Optional right-column note (label above, value below) — e.g. Gross Rev. or Avg. /day.
   * `subValue` renders as a smaller third line (e.g. the unregistered caveat), `subValueColor`
   * overrides its fill.
   */
  trailingNote?: { label: string; value: string; subValue?: string; subValueColor?: string };
  /**
   * Optional extra bar segment appended after the main bar (same scale) — e.g. confirmed but
   * unregistered payments on the Payment Succeeded step. Has its own count label and tooltip.
   */
  extraSegment?: { value: number; color: string; tooltip?: string };
}

interface FunnelChartProps {
  steps: FunnelStep[];
  stepTooltip?: (step: FunnelStep, index: number) => string;
  /**
   * Click: open in place. Middle-click or Ctrl/Cmd+click (see `e`): open in a new tab — see
   * `packages/analytics-app/src/lib/dashboardNav.ts`.
   */
  onStepClick?: (
    step: FunnelStep,
    index: number,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey">,
  ) => void;
  /** Click on a step's `extraSegment` (same new-tab gesture support as `onStepClick`). */
  onExtraSegmentClick?: (
    step: FunnelStep,
    index: number,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey">,
  ) => void;
}

function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

export function FunnelChart({ steps, stepTooltip, onStepClick, onExtraSegmentClick }: FunnelChartProps) {
  if (steps.length === 0) {
    return <p className="py-5 text-[13px] text-text-secondary">No data</p>;
  }

  const labelColRight = 148;
  const barLeft = labelColRight + 10;
  const barMaxWidth = 248;
  const pctColX = barLeft + barMaxWidth + 14;
  const revColX = pctColX + 44;
  const hasTrailingNote = steps.some((s) => s.trailingNote);
  const width = hasTrailingNote ? revColX + 88 : pctColX + 52;
  const barCenterX = barLeft + barMaxWidth / 2;
  const headerY = 8;
  const topPad = 10;
  const rowH = 38;
  const barH = 20;
  const height = topPad + steps.length * rowH + 2;

  const firstValue = steps[0]?.value ?? 0;
  const maxValue = Math.max(...steps.map((s) => s.value), 0);
  const scaleMax = firstValue > 0 ? firstValue : Math.max(1, maxValue);
  const pctDenom = firstValue > 0 ? firstValue : scaleMax;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} className="block">
      <text x={width} y={headerY} textAnchor="end" fill="#9eb2d7" fontSize="10">
        {firstValue > 0 ? "Top of funnel = 100%" : "Bar width vs largest step"}
      </text>
      {steps.map((s, i) => {
        const stepPct = s.value / scaleMax;
        const w = Math.min(barMaxWidth, Math.max(s.value > 0 ? 6 : 0, stepPct * barMaxWidth));
        const y = topPad + i * rowH;
        const rowMidY = y + rowH / 2;
        const barY = s.summary ? y + 6 : y + (rowH - barH) / 2;
        const barMidY = barY + barH / 2;
        const labelY = s.summary ? y + 10 : rowMidY + 4;
        const summaryY = y + 22;
        const countX = s.value > 0 ? barLeft + w / 2 : barCenterX;
        const countY = s.value > 0 ? barY - 2 : barMidY + 4;

        const tip =
          stepTooltip?.(s, i) ??
          (onStepClick
            ? `${s.label}: ${Math.round(s.value)} — Click: open Raw Events (filtered). Middle-click / Ctrl/Cmd+click: new tab.`
            : `${s.label}: ${Math.round(s.value)}`);

        const extra = s.extraSegment;
        const showExtra = !!extra && extra.value > 0;
        const segmentGap = 2;
        const extraX = barLeft + (s.value > 0 ? w + segmentGap : 0);
        const extraW = showExtra
          ? Math.max(5, Math.min((extra.value / scaleMax) * barMaxWidth, barMaxWidth - (extraX - barLeft)))
          : 0;
        const combinedSpanW =
          (s.value > 0 ? w : 0) + (showExtra ? (s.value > 0 ? segmentGap : 0) + extraW : 0);
        const combinedCenterX = barLeft + combinedSpanW / 2;
        const totalCount = showExtra ? s.value + extra.value : s.value;
        const pctValue = showExtra ? totalCount : s.value;

        return (
          <g key={i}>
            <text x={labelColRight} y={labelY} textAnchor="end" fill="#dce6ff" fontSize="12">
              {s.label}
            </text>
            {s.summary ? (
              <text x={labelColRight} y={summaryY} textAnchor="end" fill="#9eb2d7" fontSize="10">
                {s.summary}
              </text>
            ) : null}
            {showExtra ? (
              <text x={combinedCenterX} y={barY - 2} textAnchor="middle" fill="#9eb2d7" fontSize="11">
                {Math.round(totalCount)}
              </text>
            ) : (
              <text x={countX} y={countY} textAnchor="middle" fill="#9eb2d7" fontSize="11">
                {Math.round(s.value)}
              </text>
            )}
            {showExtra && s.value > 0 ? (
              <text
                x={barLeft + w / 2}
                y={barY + barH + 11}
                textAnchor="middle"
                fill={s.color ?? "#4d8ed8"}
                fontSize="11"
              >
                {Math.round(s.value)}
              </text>
            ) : null}
            {s.value > 0 ? (
              <rect
                x={barLeft}
                y={barY}
                width={w}
                height={barH}
                rx={5}
                fill={s.color ?? "#4d8ed8"}
                style={{ cursor: onStepClick ? "pointer" : undefined }}
                onClick={onStepClick ? (e) => onStepClick(s, i, e) : undefined}
                onAuxClick={
                  onStepClick
                    ? (e) => {
                        if (e.button !== 1) return;
                        onStepClick(s, i, e);
                      }
                    : undefined
                }
              >
                <title>{tip}</title>
              </rect>
            ) : (
              <rect
                x={barLeft}
                y={barY}
                width={barMaxWidth}
                height={barH}
                fill="transparent"
                style={{ cursor: onStepClick ? "pointer" : undefined }}
                onClick={onStepClick ? (e) => onStepClick(s, i, e) : undefined}
                onAuxClick={
                  onStepClick
                    ? (e) => {
                        if (e.button !== 1) return;
                        onStepClick(s, i, e);
                      }
                    : undefined
                }
              >
                <title>{tip}</title>
              </rect>
            )}
            {showExtra ? (
              <>
                <text
                  x={extraX + extraW / 2}
                  y={barY + barH + 11}
                  textAnchor="middle"
                  fill={extra.color}
                  fontSize="11"
                >
                  {Math.round(extra.value)}
                </text>
                <rect
                  x={extraX}
                  y={barY}
                  width={extraW}
                  height={barH}
                  rx={5}
                  fill={extra.color}
                  style={{ cursor: onExtraSegmentClick ? "pointer" : undefined }}
                  onClick={onExtraSegmentClick ? (e) => onExtraSegmentClick(s, i, e) : undefined}
                  onAuxClick={
                    onExtraSegmentClick
                      ? (e) => {
                          if (e.button !== 1) return;
                          onExtraSegmentClick(s, i, e);
                        }
                      : undefined
                  }
                >
                  <title>{extra.tooltip ?? `${s.label} (extra): ${Math.round(extra.value)}`}</title>
                </rect>
              </>
            ) : null}
            <text x={pctColX} y={rowMidY + 4} textAnchor="start" fill="#dce6ff" fontSize="12">
              {fmtPct(pctValue / pctDenom)}
            </text>
            {s.trailingNote ? (
              <>
                <text
                  x={revColX}
                  y={s.trailingNote.subValue ? rowMidY - 9 : rowMidY - 5}
                  textAnchor="start"
                  fill="#9eb2d7"
                  fontSize="9"
                >
                  {s.trailingNote.label}
                </text>
                <text
                  x={revColX}
                  y={s.trailingNote.subValue ? rowMidY + 2 : rowMidY + 7}
                  textAnchor="start"
                  fill="#dce6ff"
                  fontSize="11"
                >
                  {s.trailingNote.value}
                </text>
                {s.trailingNote.subValue ? (
                  <text
                    x={revColX}
                    y={rowMidY + 13}
                    textAnchor="start"
                    fill={s.trailingNote.subValueColor ?? "#9eb2d7"}
                    fontSize="9"
                  >
                    {s.trailingNote.subValue}
                  </text>
                ) : null}
              </>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
